import { getEffectiveAuthorities } from '../Product.js';

/**
 * SessionStorage - Manages user session data in sessionStorage
 * Single source of truth for user data
 */
export class SessionStorage {
  static SELECTED_CLINIC_KEY = 'selectedClinicId';
  static USER_KEY = 'user';

  constructor() {
    this.USER_KEY = SessionStorage.USER_KEY;
    this.AUTH_REDIRECT_KEY = 'auth_redirect';
  }

  /**
   * Get user data from session storage
   * @returns {Object|null} User data or null if not found/invalid
   */
  getUserData() {
    const stored = SessionStorage.readRawUserData();
    if (!stored) {
      console.log('[SessionStorage] No user data in session storage');
      return null;
    }

    if (!this.isValid(stored)) {
      console.warn('[SessionStorage] Invalid user data in session storage, removing');
      this.clearUserData();
      return null;
    }

    console.log('[SessionStorage] Valid user data found:', { userId: stored.userId, clinicIds: stored.clinicIds });
    return stored;
  }

  /**
   * Set user data in session storage
   * @param {Object} userData - User data to store
   */
  setUserData(userData) {
    const clinicIds = userData.clinicIds || userData.clinic_ids || [];
    const clinicProducts = userData.clinicProducts || userData.clinic_products || {};

    const normalized = {
      userId: userData.userId || userData.user_id,
      email: userData.email,
      name: userData.name,
      nhsUserId: userData.nhsUserId || userData.nhs_user_id,
      outOfOffice: userData.outOfOffice ?? null,
      clinicIds: clinicIds,
      authorities: userData.authorities,
      clinicProducts: clinicProducts
    };

    console.log('[SessionStorage] Storing user data:', { userId: normalized.userId, clinicIds: normalized.clinicIds });
    sessionStorage.setItem(this.USER_KEY, JSON.stringify(normalized));

    // Set default selected clinic (must be non-MASTER) if none is selected yet
    const defaultClinicId = clinicIds.find((id) => id !== 'MASTER');
    if (!SessionStorage.getSelectedClinicId() && defaultClinicId != null) {
      SessionStorage.setSelectedClinicId(defaultClinicId);
    } else {
      SessionStorage.persistEffectiveAuthorities();
    }

    const stored = SessionStorage.readRawUserData();
    window.dispatchEvent(new CustomEvent('userDataUpdated', {
      detail: { userData: stored }
    }));
  }

  /**
   * Clear user data from session storage
   */
  clearUserData() {
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(SessionStorage.SELECTED_CLINIC_KEY);
  }

  /**
   * Check if user data is valid and complete
   * @param {Object} userData - User data to validate
   * @returns {boolean} True if valid
   */
  isValid(userData) {
    if (!userData || typeof userData !== 'object') {
      return false;
    }

    const requiredFields = ['userId', 'email', 'name', 'clinicIds', 'authorities', 'clinicProducts'];
    return requiredFields.every(field => userData[field] != null);
  }

  /**
   * Check if user is authenticated (has valid session data)
   * @returns {boolean} True if authenticated
   */
  isAuthenticated() {
    return this.getUserData() !== null;
  }

  static readRawUserData() {
    const userData = sessionStorage.getItem(SessionStorage.USER_KEY);
    if (!userData) {
      return null;
    }
    try {
      return JSON.parse(userData);
    } catch (error) {
      console.error('[SessionStorage] Failed to parse user data from session storage:', error);
      return null;
    }
  }

  static persistEffectiveAuthorities() {
    const stored = SessionStorage.readRawUserData();
    if (!stored) {
      return null;
    }
    stored.effectiveAuthorities = getEffectiveAuthorities(
      stored.authorities,
      stored.clinicProducts,
      SessionStorage.getSelectedClinicId()
    );
    sessionStorage.setItem(SessionStorage.USER_KEY, JSON.stringify(stored));
    return stored;
  }

  static toAuthUser(stored) {
    if (!stored) {
      return null;
    }
    return {
      ...stored,
      authorities: stored.effectiveAuthorities || []
    };
  }

  static getSelectedClinicId() {
    return sessionStorage.getItem(SessionStorage.SELECTED_CLINIC_KEY);
  }

  static setSelectedClinicId(clinicId) {
    sessionStorage.setItem(SessionStorage.SELECTED_CLINIC_KEY, clinicId);
    const stored = SessionStorage.persistEffectiveAuthorities();
    window.dispatchEvent(new CustomEvent('userDataUpdated', {
      detail: { userData: stored }
    }));
    window.dispatchEvent(new CustomEvent('clinicChanged', {
      detail: { clinicId }
    }));
  }

  /**
   * Set auth redirect flag to prevent redirect loops
   */
  setAuthRedirect() {
    sessionStorage.setItem(this.AUTH_REDIRECT_KEY, 'true');
  }

  /**
   * Clear auth redirect flag
   */
  clearAuthRedirect() {
    sessionStorage.removeItem(this.AUTH_REDIRECT_KEY);
  }

  /**
   * Check if auth redirect is in progress
   * @returns {boolean} True if redirect flag is set
   */
  isAuthRedirectInProgress() {
    return sessionStorage.getItem(this.AUTH_REDIRECT_KEY) === 'true';
  }
}
